import Vector from "victor";
import { Tool } from "@/wbcontroller/types/Tool";
import ToolType from "@/wbcontroller/types/ToolType";
import WhiteboardControllerInterface from "../types/WhiteboardControllerInterface";
import { ShapeType } from "../types/ShapeType";
import {
  SimpleShape2,
  LineShape2,
  RectShape2,
  DiamondShape2,
  EllipseShape2,
} from "./StandardShapes";
import { WBHUDState } from "@/store/WBHUDState";
import { Scope } from "@/wbcontroller/types/Scope";
import {
  SimplePointerEventHandler,
  SimplePointerEventHandlerDelegate,
} from "../SimplePointerEventHandler";
import AddStandardShapeOperation from "./AddStandardShapeOperation";
import ThrottledUpsertController from "../toolHelpers/ThrottledUpsertController";
import AnimationFrameMoveController from "../toolHelpers/AnimationFrameMoveController";
import { LineCap } from "./LineCap";

abstract class StandardShapeTool implements SimplePointerEventHandlerDelegate {
  // Populated by bind()
  wb?: WhiteboardControllerInterface;
  eventHandler: SimplePointerEventHandler;
  throttledUpsertController: ThrottledUpsertController;
  animationFrameMoveController: AnimationFrameMoveController;

  // Subclass should replace this and mutate it whenever settings change
  shapePrototype: SimpleShape2 = {
    shapeType: ShapeType.Rect,
    strokeColor: "#ff0000",
    strokeWidth: 20,
    pointA: [0, 0],
    pointB: [0, 0],
    boundingBoxOrigin: [0, 0],
    boundingBoxSize: [0, 0],
    boundingBoxOriginalSize: [0, 0],
    translate: [0, 0],
    zIndex: 0,
  };

  // Temporary state during an interaction
  isPointerDown = false;

  // Shape info
  shapeID = "";
  shape: SimpleShape2;

  constructor() {
    this.throttledUpsertController = new ThrottledUpsertController(
      (scope: Scope) => {
        if (!this.wb) return;

        this.wb.shapeStore.sendUpsertMany(scope, this.shapeID, this.shape);
      }
    );

    this.animationFrameMoveController = new AnimationFrameMoveController(
      this.onAnimationFrame
    );

    this.eventHandler = new SimplePointerEventHandler();
    // appease typescript
    this.shape = Object.assign({}, this.shapePrototype);
  }

  // Settings passed down from
  // Vuex -> Whiteboard.vue -> WhiteboardController -> this method
  settingsDidChange(settings: WBHUDState) {
    this.shapePrototype.strokeColor = settings.strokeColor;
    this.shapePrototype.strokeWidth = settings.strokeWidth;
  }

  nextZIndexDidChange(nextZIndex: number) {
    this.shapePrototype.zIndex = nextZIndex;
  }

  onPointerDown = (point: Vector) => {
    if (!this.wb) return;
    this.isPointerDown = true;
    this.animationFrameMoveController.onPointerDown(point);

    // Set up pen shape to be used while user is actively drawing
    this.shapeID = this.wb.makeID();
    const origin = this.wb.camera.convertScreenToWorld(new Vector(0, 0));
    const pointA = point;
    const pointB = point;

    this.shape = Object.assign({}, this.shapePrototype, {
      pointA: pointA.toArray(),
      pointB: pointB.toArray(),
      boundingBoxOrigin: origin.toArray(),
      boundingBoxSize: this.wb.camera
        .convertScreenToWorld(this.wb.camera.viewportSize)
        .subtract(origin)
        .toArray(),
      boundingBoxOriginalSize: this.wb.camera.viewportSize.toArray(),
    });

    // Dispatch event to make things show up
    this.throttledUpsertController.call();
  };

  onPointerMove = (point: Vector) => {
    this.animationFrameMoveController.onPointerMove(point);
  };

  onAnimationFrame = (point: Vector) => {
    if (!this.wb) return;
    if (!this.isPointerDown) return;

    // Ignore this frame if mouse hasn't moved
    if (Vector.fromArray(this.shape.pointB).isEqualTo(point)) return;

    this.shape.pointB = point.toArray() as [number, number];

    this.throttledUpsertController.call();
  };

  onPointerUp = () => {
    this.throttledUpsertController.cancel();

    if (!this.wb) return;
    if (!this.isPointerDown) return;
    this.isPointerDown = false;
    this.animationFrameMoveController.onPointerUp();

    // Dispatch event to create the new shape

    const op = new AddStandardShapeOperation(
      this.wb.makeID(),
      this.wb.camera,
      this.shape
    );
    this.wb.applyOperation(op);

    // Dispatch events to delete the live shape
    this.wb.shapeStore.sendDelete(Scope.Live, this.shapeID);

    this.didCreateShape(this.wb, this.shapeID);

    this.shapeID = "";
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  didCreateShape(wb: WhiteboardControllerInterface, shapeID: string) {
    // subclass may override, for example to focus the element
  }

  abort() {
    this.isPointerDown = false;

    this.animationFrameMoveController.abort();
    this.throttledUpsertController.cancel();

    this.wb?.shapeStore.sendDelete(Scope.Live, this.shapeID);

    this.shapeID = "";
  }

  bind(wb: WhiteboardControllerInterface) {
    this.wb = wb;
    this.eventHandler.bind(wb, this);
  }

  unbind() {
    this.wb = undefined;
    this.eventHandler.unbind();
  }
}

export class ArrowTool extends StandardShapeTool implements Tool {
  // Discriminated union marker
  toolType = ToolType.Arrow;

  // New shapes are copied from this object
  shapePrototype: LineShape2 = {
    shapeType: ShapeType.Line,
    strokeColor: "#ff0000",
    strokeWidth: 20,
    pointA: [0, 0],
    pointB: [0, 0],
    lineCapA: LineCap.Round,
    lineCapB: LineCap.Arrow,

    // properties below this line are set when drawing begins
    translate: [0, 0],
    zIndex: 0,
    boundingBoxOrigin: [0, 0],
    boundingBoxSize: [0, 0],
    boundingBoxOriginalSize: [0, 0],
  };
}

export class LineTool extends StandardShapeTool implements Tool {
  // Discriminated union marker
  toolType = ToolType.Line;

  // New shapes are copied from this object
  shapePrototype: LineShape2 = {
    shapeType: ShapeType.Line,
    strokeColor: "#ff0000",
    strokeWidth: 20,
    pointA: [0, 0],
    pointB: [0, 0],
    lineCapA: LineCap.Round,
    lineCapB: LineCap.Round,

    // properties below this line are set when drawing begins
    translate: [0, 0],
    zIndex: 0,
    boundingBoxOrigin: [0, 0],
    boundingBoxSize: [0, 0],
    boundingBoxOriginalSize: [0, 0],
  };
}

export class RectTool extends StandardShapeTool implements Tool {
  // Discriminated union marker
  toolType = ToolType.Rect;

  // New shapes are copied from this object
  shapePrototype: RectShape2 = {
    shapeType: ShapeType.Rect,
    strokeColor: "#ff0000",
    strokeWidth: 20,
    fillColor: "rgba(0, 0, 0, 0)",
    pointA: [0, 0],
    pointB: [0, 0],

    // properties below this line are set when drawing begins
    translate: [0, 0],
    zIndex: 0,
    boundingBoxOrigin: [0, 0],
    boundingBoxSize: [0, 0],
    boundingBoxOriginalSize: [0, 0],
  };

  settingsDidChange(settings: WBHUDState) {
    super.settingsDidChange(settings);
    this.shapePrototype.fillColor = settings.fillColor;
  }
}

export class DiamondTool extends StandardShapeTool implements Tool {
  // Discriminated union marker
  toolType = ToolType.Diamond;

  // New shapes are copied from this object
  shapePrototype: DiamondShape2 = {
    shapeType: ShapeType.Diamond,
    strokeColor: "#ff0000",
    strokeWidth: 20,
    fillColor: "rgba(0, 0, 0, 0)",
    pointA: [0, 0],
    pointB: [0, 0],

    // properties below this line are set when drawing begins
    translate: [0, 0],
    zIndex: 0,
    boundingBoxOrigin: [0, 0],
    boundingBoxSize: [0, 0],
    boundingBoxOriginalSize: [0, 0],
  };

  settingsDidChange(settings: WBHUDState) {
    super.settingsDidChange(settings);
    this.shapePrototype.fillColor = settings.fillColor;
  }
}

export class EllipseTool extends StandardShapeTool implements Tool {
  // Discriminated union marker
  toolType = ToolType.Ellipse;

  // New shapes are copied from this object
  shapePrototype: EllipseShape2 = {
    shapeType: ShapeType.Ellipse,
    strokeColor: "#ff0000",
    strokeWidth: 20,
    fillColor: "rgba(0, 0, 0, 0)",
    pointA: [0, 0],
    pointB: [0, 0],

    // properties below this line are set when drawing begins
    translate: [0, 0],
    zIndex: 0,
    boundingBoxOrigin: [0, 0],
    boundingBoxSize: [0, 0],
    boundingBoxOriginalSize: [0, 0],
  };

  settingsDidChange(settings: WBHUDState) {
    super.settingsDidChange(settings);
    this.shapePrototype.fillColor = settings.fillColor;
  }
}
